/**
 * WeeklyBoardTeamView.jsx — v194 Phase 2 (team-facing read-only view)
 *
 * يعرض بنود الاجتماع الأسبوعي القادم للفريق ضمن صفحة الاجتماعات:
 *   • النسخة المختصرة فقط (لا موسّعة، ولا زر مطوي — التوسّع للـ CEO في صفحته)
 *   • البنود المعتمدة والمرتبة كما رتّبها CEO — لا يستطيع الفريق التعديل
 *   • مهام المستخدم الحالي فقط ضمن الاجتماع — لا مهام غيره
 *
 * مصدر الحقيقة: نفس endpoints البنود التي تستخدمها لوحة CEO — بلا نسخ
 * ولا تكرار في DB. أي تعديل من CEO يظهر هنا فوراً عند إعادة التحميل.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "./lib/api";
import { formatKSADate, formatKSADateTime } from "./lib/ksaTime";

const GOLD = "#c8963e";
const BROWN = "#5c3a1e";
const DEEP = "#231709";
const CREAM = "#faf6ec";
const BORDER = "#e6d9b8";
const OK = "#059669";

const s = {
  wrap: {
    marginBottom: 18,
    display: "flex", flexDirection: "column",
    gap: "var(--m-sp-2, 12px)",
  },
  banner: {
    background: `linear-gradient(135deg, ${DEEP} 0%, ${BROWN} 100%)`,
    borderRadius: 14, padding: "var(--m-sp-3, 20px)",
    borderRight: `4px solid ${GOLD}`,
  },
  bannerTitle: {
    color: GOLD, fontWeight: 900,
    fontSize: "var(--m-fs-h3, 18px)",
  },
  bannerSubtitle: {
    color: "#d9c9a8", fontSize: "var(--m-fs-caption, 14px)",
    marginTop: 4,
  },
  card: {
    background: "#fff", border: `1px solid ${BORDER}`,
    borderRadius: 12, padding: "var(--m-sp-3, 20px)",
  },
  itemCard: {
    background: "#fff", border: `1px solid ${BORDER}`,
    borderRadius: 12, padding: "var(--m-sp-3, 20px)",
    marginBottom: "var(--m-sp-2, 12px)",
  },
  orderBadge: {
    display: "inline-block",
    background: `${GOLD}20`, color: BROWN,
    padding: "3px 10px", borderRadius: 999,
    fontSize: "var(--m-fs-micro, 12px)", fontWeight: 800,
    marginLeft: 8,
  },
  title: {
    fontSize: "var(--m-fs-h3, 18px)", fontWeight: 800,
    color: DEEP, lineHeight: 1.4,
  },
  summary: {
    fontSize: "var(--m-fs-caption, 14px)", color: "#5c4a35",
    lineHeight: 1.7, marginTop: 8,
  },
  primaryNumber: {
    marginTop: 10, background: CREAM, borderRadius: 10,
    padding: "10px 14px", display: "inline-block",
  },
  taskCard: {
    background: `${OK}0d`, border: `1px solid ${OK}33`,
    borderRadius: 10, padding: 12, marginTop: 8,
  },
};

export default function WeeklyBoardTeamView() {
  const mid = "weekly_ops";
  const [meeting, setMeeting] = useState(null);
  const [items, setItems] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ag, mt] = await Promise.all([
        api.get(`/command-room/meetings/${mid}/agenda?view=brief`),
        api.get(`/command-room/meetings/${mid}/tasks/mine`),
      ]);
      setMeeting({ next_at: ag.data.next_at });
      setItems(ag.data.items || []);
      setMyTasks(mt.data.items || []);
    } catch (e) {
      // Non-blocking — if agenda endpoint fails, hide the section
      setError(e?.response?.data?.detail || "تعذّر تحميل بنود الاجتماع");
    } finally { setLoading(false); }
  }, [mid]);
  useEffect(() => { load(); }, [load]);

  // If there's no agenda at all AND user has no tasks — hide silently
  if (loading) return null;
  if (error || (!items.length && !myTasks.length)) return null;

  const formattedDate = meeting?.next_at ? formatKSADateTime(new Date(meeting.next_at), {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    }) : "—";

  return (
    <div dir="rtl" style={s.wrap} data-testid="wmb-team-view">
      {/* Banner */}
      <div style={s.banner}>
        <div style={s.bannerTitle}>📅 الاجتماع الأسبوعي القادم</div>
        <div style={s.bannerSubtitle}>الموعد: {formattedDate}</div>
      </div>

      {/* Items */}
      {items.length > 0 && (
        <div>
          <div style={{ fontSize: "var(--m-fs-caption, 14px)", fontWeight: 700, color: BROWN, marginBottom: 8, marginTop: 4 }}>
            📋 بنود الاجتماع ({items.length})
          </div>
          {items.map((it) => (
            <AgendaItemCard key={it.id} item={it} />
          ))}
        </div>
      )}

      {/* My tasks */}
      <div style={{ marginTop: "var(--m-sp-2, 12px)" }}>
        <div style={{ fontSize: "var(--m-fs-caption, 14px)", fontWeight: 700, color: BROWN, marginBottom: 8 }}>
          ✅ مهامي في هذا الاجتماع ({myTasks.length})
        </div>
        {myTasks.length === 0 ? (
          <div style={{ ...s.card, textAlign: "center", color: "#8a6d4d",
                        fontSize: "var(--m-fs-caption, 14px)" }} data-testid="wmb-team-notasks">
            لا توجد مهام مسندة إليك لهذا الاجتماع بعد.
          </div>
        ) : (
          myTasks.map((t) => (
            <div key={t.id} style={s.taskCard} data-testid={`wmb-team-task-${t.id}`}>
              <div style={{ fontSize: "var(--m-fs-body, 16px)", fontWeight: 700, color: DEEP, lineHeight: 1.5 }}>
                {t.title}
              </div>
              {t.approved_at && (
                <div style={{ fontSize: "var(--m-fs-micro, 12px)", color: "#5c4a35", marginTop: 6 }}>
                  ✅ أُسندت إليك في {formatKSADate(new Date(t.approved_at))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function AgendaItemCard({ item }) {
  const brief = item.brief || {};
  const pn = brief.primary_number;
  return (
    <div style={s.itemCard} data-testid={`wmb-team-item-${item.id}`}>
      <div>
        <span style={s.orderBadge}>#{item.order}</span>
        <span style={s.title}>{brief.title || "بند بلا عنوان"}</span>
      </div>
      {brief.summary && <div style={s.summary}>{brief.summary}</div>}
      {pn && (
        <div style={s.primaryNumber}>
          <span style={{ fontSize: "var(--m-fs-caption, 14px)", color: "#8a6d4d" }}>{pn.label}: </span>
          <strong style={{ fontSize: "var(--m-fs-h3, 18px)", color: BROWN }}>{Number(pn.value || 0).toLocaleString("en")}</strong>
          <span style={{ fontSize: "var(--m-fs-micro, 12px)", color: "#8a6d4d", marginRight: 4 }}> {pn.unit}</span>
        </div>
      )}
    </div>
  );
}
